import { Injectable } from '@angular/core';
import { Observable, of, Subject, throwError } from 'rxjs';
import { catchError, map, mergeMap, take } from 'rxjs/operators';
import { AuthRedirectService } from 'src/app/shared';
import { AuthService, FunctionsService, ProfileConst, User, UserSolverExceptionCode } from 'src/app/utils';
import { HttpMethods } from 'src/app/utils/cargo-driver-check-in-service/services/functions.service';
import { LoginExceptionCode } from '../types/login-exception-code.enum';
import { DeviceUuidService } from './device-uuid.service';

@Injectable()
export class LoginService {

    private userLoginAttempt: Subject<number>;

    constructor(
        private authService: AuthService,
        private authRedirectService: AuthRedirectService,
        private functionsService: FunctionsService,
        private deviceUuidService: DeviceUuidService,
    ) {
        this.userLoginAttempt = new Subject<number>();
    }

    emitUserLoginAttempt(): void {
        this.userLoginAttempt.next(+new Date());
    }

    onUserLoginAttempt(): Observable<number> {
        return this.userLoginAttempt.asObservable();
    }

    loginDevice(): Observable<boolean> {
        return this.deviceUuidService.getDeviceUUID()
            .pipe(
                take(1),
                mergeMap(uid => {
                    return this.functionsService.request<string>(HttpMethods.POST, 'app/loginDevice', {
                        body: { uid },
                        responseType: 'text'
                    })
                        .pipe(
                            take(1),
                            mergeMap(token => {
                                if (!token) {
                                    return this.authService.logoutIfNeeded()
                                        .pipe(
                                            map(() => false)
                                        );
                                }

                                return this.authService.loginTokenDevice(token)
                                    .pipe(
                                        take(1),
                                        map(() => true)
                                    );
                            }),
                            catchError(() => of(false))
                        );
                })
            );
    }

    login(email: string, password: string): Observable<User> {
        return this.authService.login(email, password)
            .pipe(
                catchError(error => {
                    if (!(error && error.code && error.message)) {
                        return throwError(() => ({
                            code: LoginExceptionCode.UNKNOWN,
                            message: 'Something went wrong!'
                        }));
                    }

                    switch (error.code) {
                        case 'auth/wrong-password':
                        case 'auth/user-not-found':
                        case 'auth/invalid-login-credentials':
                        case 'auth/invalid-credential':
                            return throwError(() => ({
                                code: LoginExceptionCode.INVALID_USER_OR_PASSWORD,
                                message: 'The password or the user is invalid.'
                            }));
                        case 'auth/invalid-email':
                            return throwError(() => ({
                                code: LoginExceptionCode.INVALID_EMAIL,
                                message: error.message
                            }));
                        case 'appCheck/fetch-status-error':
                            return throwError(() => ({
                                code: LoginExceptionCode.UNABLE_TO_GET_USER,
                                message: 'Action denied, something went wrong'
                            }));
                        case 'auth/network-request-failed':
                            return throwError(() => ({
                                code: LoginExceptionCode.NETWORK_REQUEST_FAILED,
                                message: `Network error. Make sure you're connected and try again.`
                            }));
                        case 'auth/visibility-check-was-unavailable.-please-retry-the-request-and-contact-support-if-the-problem-persists':
                        case 'auth/visibility-check-was-unavailable':
                            return throwError(() => ({
                                code: LoginExceptionCode.VISIBILITY_CHECK_WAS_UNAVAILABLE,
                                message: `There was a problem verifying your session. Please try again. If the error persists, contact support.`
                            }));
                        case UserSolverExceptionCode.GET_USER:
                        case UserSolverExceptionCode.GET_DRIVER:
                        case UserSolverExceptionCode.UNAUTHENTICATED:
                        case UserSolverExceptionCode.UNAVAILABLE:
                        case UserSolverExceptionCode.TIMEOUT:
                        case UserSolverExceptionCode.UNKNOWN:
                            return this.authService.logout()
                                .pipe(
                                    mergeMap(() =>
                                        throwError(() => ({
                                            code: LoginExceptionCode.UNABLE_TO_GET_USER,
                                            message: error.message
                                        }))
                                    )
                                );
                        case 'auth/user-disabled':
                            return throwError(() => ({
                                code: LoginExceptionCode.USER_DISABLED,
                                message: 'The user account has been disabled, please contact the administrator of your organization'
                            }))
                        default: {
                            const blockedRefererRegex = /^auth\/requests-from-referer-https?:\/\/[^ ]+-are-blocked$/;

                            if (blockedRefererRegex.test(error.code)) {
                                return throwError(() => ({
                                    code: LoginExceptionCode.REQUESTS_FROM_REFERER_BLOCKED,
                                    message: 'Login requests from this page are blocked. Please contact support.'
                                }));
                            }

                            return throwError(() => ({
                                code: LoginExceptionCode.UNKNOWN,
                                message: error.message
                            }));
                        }
                    }
                }),
                mergeMap(user => {
                    if (
                        user
                        && !user.warehouse?.wh_disable
                        && (
                            this.authRedirectService.isProfileValid()
                            || user.usr_profile === ProfileConst.driver_pf
                        )
                    ) {
                        return of(user);
                    }

                    if (!user) {
                        return of(null)
                    }

                    return this.authService.logout()
                        .pipe(
                            mergeMap(() => {
                                if (user.warehouse?.wh_disable) {
                                    return throwError(() => ({
                                        code: LoginExceptionCode.WAREHOUSE_DISABLED,
                                        message: 'Cargo facility de-activate, please contact support.'
                                    }));
                                }

                                return throwError(() => ({
                                    code: LoginExceptionCode.UNSUPPORTED_PROFILE,
                                    message: 'User profile not supported.'
                                }));
                            })
                        );
                })
            );
    }

    logout(): Observable<string> {
        return this.authService.logoutIfNeeded();
    }

    validateReCaptchaLogin(userEmail: string, recaptchaToken: string): Observable<void> {
        return this.functionsService.post<void>(`api/login/validate-recaptcha`, {
            usr_email: userEmail,
            recaptcha_token: recaptchaToken,
        });
    }
}
